import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import {
  Badge,
  Button,
  buttonClass,
  Card,
  type Column,
  DataTable,
  FormRow,
  focusRing,
  Input,
  Notice,
  PageTitle,
  SectionTitle,
  Spinner,
  Textarea,
  textComparator,
  useWide,
} from "../../ui/index.js";
import { fmtDateTime, fmtMoney, useCabinet } from "../cabinet/index.js";
import { BookingSheet, instagramLink, statusTone } from "../calendar/index.js";
import { stateTone } from "../conversations/index.js";

const small = "px-3 py-2 text-sm";

/**
 * Карточка клиента: контакты, заметки, неявки, визиты (мастер видит только свои), диалоги,
 * склейка с другой карточкой по телефону или @нику. Данные о здоровье в карточке не храним.
 * На широком экране две колонки: слева форма и склейка, справа визиты таблицей с сортировкой.
 */
export function ClientCardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const wide = useWide();
  const { clientId = "" } = useParams();
  const navigate = useNavigate();
  const cab = useCabinet();
  const card = useQuery(trpc.calendar.clients.card.queryOptions({ clientId }));
  const extra = useQuery(trpc.cabinet.client.queryOptions({ clientId }));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId, includeInactive: true }));
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [merge, setMerge] = useState("");
  const [bookingId, setBookingId] = useState<string | null>(null);

  const client = card.data?.client;
  // Форма заполняется один раз на карточку: фоновое обновление запроса не должно стирать набранное.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  useEffect(() => {
    if (client && loadedFor !== client.id) {
      setLoadedFor(client.id);
      setName(client.name ?? "");
      setPhone(client.phone ?? "");
      setNotes(client.notes ?? "");
    }
  }, [client, loadedFor]);

  const refresh = () => {
    void queryClient.invalidateQueries(trpc.calendar.clients.pathFilter());
    void queryClient.invalidateQueries(trpc.cabinet.client.pathFilter());
  };
  const update = useMutation(
    trpc.calendar.clients.update.mutationOptions({
      onSuccess: () => {
        setLoadedFor(null);
        refresh();
      },
    }),
  );
  const doMerge = useMutation(
    trpc.calendar.clients.merge.mutationOptions({
      onSuccess: (c) => {
        refresh();
        setMerge("");
        if (c.id !== clientId) navigate(`${cab.base}/clients/${c.id}`, { replace: true });
      },
    }),
  );

  if (card.isPending) return <Spinner />;
  if (card.isError || !client) return <Notice kind="error">{t("clients.notFoundOne")}</Notice>;
  const instagram = extra.data?.brief.instagram ?? null;
  const masterName = (id: string) => masters.data?.find((m) => m.id === id)?.name ?? "";
  const dirty = name !== (client.name ?? "") || phone !== (client.phone ?? "") || notes !== (client.notes ?? "");
  type Visit = (typeof card.data.visits)[number];
  const price = (v: Visit) => v.items.reduce((s, i) => s + i.price.amount, 0);
  const text = textComparator(lang);
  const statusLabel = (v: Visit) => t(`cal.status.${v.status}`);
  const visitColumns: Column<Visit>[] = [
    {
      key: "date",
      title: t("clients.colDate"),
      sort: (a, b) => a.period.start.getTime() - b.period.start.getTime(),
      render: (v) => <span className="font-medium">{fmtDateTime(v.period.start, cab.timezone, lang)}</span>,
    },
    { key: "services", title: t("clients.colServices"), render: (v) => v.items.map((i) => i.name).join(" + ") },
    {
      key: "master",
      title: t("clients.colMaster"),
      sort: (a, b) => text(masterName(a.masterId), masterName(b.masterId)),
      render: (v) => masterName(v.masterId),
    },
    { key: "price", title: t("clients.colPrice"), sort: (a, b) => price(a) - price(b), className: "text-right", render: (v) => fmtMoney(price(v), lang) },
    {
      key: "status",
      title: t("clients.colStatus"),
      sort: (a, b) => text(statusLabel(a), statusLabel(b)),
      render: (v) => <Badge tone={statusTone[v.status]}>{t(`cal.status.${v.status}`)}</Badge>,
    },
  ];

  return (
    <div>
      <PageTitle>{client.name || (instagram ? `@${instagram}` : t("clients.noName"))}</PageTitle>
      <div className="mb-4 flex flex-wrap gap-2">
        {client.noShowCount > 0 && <Badge tone="red">{t("clients.noShows", { count: client.noShowCount })}</Badge>}
        {instagram && (
          <a href={instagramLink(instagram)} target="_blank" rel="noreferrer" className={buttonClass("secondary", small)}>
            @{instagram}
          </a>
        )}
        {client.phone && (
          <a href={`tel:${client.phone}`} className={buttonClass("secondary", small)}>
            {t("cal.call")}
          </a>
        )}
        {extra.data?.conversations.map((c) => (
          <Link key={c.id} to={`${cab.base}/conversations/${c.id}`} className={buttonClass("secondary", small)}>
            {t("clients.chat")} <Badge tone={stateTone[c.state]}>{t(`conv.state.${c.state}`)}</Badge>
          </Link>
        ))}
      </div>

      <div className="lg:grid lg:grid-cols-2 lg:gap-6">
        <div className="lg:col-start-1">
          <Card className="space-y-3">
            <FormRow label={t("clients.name")} htmlFor="c-name">
              <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} />
            </FormRow>
            <FormRow label={t("clients.phone")} htmlFor="c-phone">
              <Input id="c-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </FormRow>
            <FormRow label={t("clients.notes")} htmlFor="c-notes" hint={t("clients.notesHint")} hintClass="mt-1 text-xs text-slate-500">
              <Textarea id="c-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </FormRow>
            {update.isError && <Notice kind="error">{trpcErrorCode(update.error) === "conflict" ? t("clients.phoneTaken") : t("common.error")}</Notice>}
            <Button
              className="w-full lg:w-auto"
              disabled={!dirty || update.isPending}
              onClick={() => update.mutate({ clientId, name: name.trim() || null, phone: phone.trim() || null, notes: notes.trim() || null })}
            >
              {t("settings.save")}
            </Button>
          </Card>
        </div>

        <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <SectionTitle>{t("clients.visits")}</SectionTitle>
          {card.data.visits.length === 0 && <Notice>{t("clients.noVisits")}</Notice>}
          {wide && card.data.visits.length > 0 && (
            <DataTable
              label={t("clients.visits")}
              columns={visitColumns}
              rows={card.data.visits}
              rowKey={(v) => v.id}
              selectedKey={bookingId}
              onRowClick={(v) => setBookingId(v.id)}
              rowLabel={(v) => fmtDateTime(v.period.start, cab.timezone, lang)}
              defaultSort={{ key: "date", dir: "desc" }}
            />
          )}
          <div className="space-y-2">
            {!wide &&
              card.data.visits.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setBookingId(v.id)}
                  className={`block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-slate-400 ${focusRing}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{fmtDateTime(v.period.start, cab.timezone, lang)}</span>
                    <Badge tone={statusTone[v.status]}>{t(`cal.status.${v.status}`)}</Badge>
                  </div>
                  <div className="text-sm text-slate-500">
                    {v.items.map((i) => i.name).join(" + ")} · {masterName(v.masterId)} ·{" "}
                    {fmtMoney(
                      v.items.reduce((s, i) => s + i.price.amount, 0),
                      lang,
                    )}
                  </div>
                </button>
              ))}
          </div>
        </div>
        <div className="lg:col-start-1">
          <SectionTitle>{t("clients.mergeTitle")}</SectionTitle>
          <Card className="space-y-2">
            <p className="text-sm text-slate-600">{t("clients.mergeHint")}</p>
            <Input value={merge} onChange={(e) => setMerge(e.target.value)} placeholder="@marko · 064 123 4567" />
            {doMerge.isError && <Notice kind="error">{trpcErrorCode(doMerge.error) === "validation" ? t("cal.badContact") : t("common.error")}</Notice>}
            {doMerge.isSuccess && <Notice kind="success">{t("clients.merged")}</Notice>}
            <Button
              variant="secondary"
              className="w-full lg:w-auto"
              disabled={!merge.trim() || doMerge.isPending}
              onClick={() => doMerge.mutate({ clientId, phoneOrHandle: merge.trim() })}
            >
              {t("clients.merge")}
            </Button>
          </Card>
        </div>
      </div>

      <BookingSheet bookingId={bookingId} onClose={() => setBookingId(null)} />
    </div>
  );
}
